// src/phase4/entities/TowerSprite.ts
// Tower entity class - handles visual rendering of turret only (base is separate)

import Phaser from 'phaser';
import { TowerInstance, TowerType, TowerLevelConfig } from '../types/tower';
import { getTowerLevelConfig } from '../data/tower-configs';

export interface TowerSpriteConfig {
  id: string;
  type: TowerType;
  x: number;
  y: number;
  pathName?: string;
  scene: Phaser.Scene;
}

export class TowerSprite extends Phaser.GameObjects.Container {
  private config: TowerSpriteConfig;
  private towerInstance: TowerInstance;
  private levelConfig: TowerLevelConfig | undefined = undefined;

  // Visual elements
  private turretSprite?: Phaser.GameObjects.Sprite;
  private turretGraphics?: Phaser.GameObjects.Graphics; // Fallback graphics when no texture
  private rangeIndicator?: Phaser.GameObjects.Graphics;
  private levelIndicator?: Phaser.GameObjects.Text;

  // State
  private isSelected: boolean = false;

  constructor(scene: Phaser.Scene, config: TowerSpriteConfig) {
    super(scene, config.x, config.y);
    this.config = config;

    // Create tower instance
    this.towerInstance = {
      id: config.id,
      type: config.type,
      currentLevel: 1,
      x: config.x,
      y: config.y,
      targetId: null,
      lastFireTime: 0,
    };

    this.init();
  }

  private init(): void {
    // Get level config
    this.updateLevelConfig();

    // Create visual elements
    this.createTurret();
    this.createLevelIndicator();

    // Set initial position
    this.setPosition(this.config.x, this.config.y);

    // Make interactive with hit area
    this.setSize(64, 64);
    this.setInteractive();

    // Handle click using Container's native events
    this.on('pointerdown', () => {
      this.onSelect();
    });
  }

  private updateLevelConfig(): void {
    const pathName = this.config.pathName || this.getDefaultPathName();
    const newLevelConfig = getTowerLevelConfig(this.config.type, pathName, this.towerInstance.currentLevel);

    // Check if texture changed
    const oldTextureKey = this.levelConfig?.textureKey;
    const newTextureKey = newLevelConfig?.textureKey;

    this.levelConfig = newLevelConfig;

    // If texture changed, recreate turret with new texture
    if (oldTextureKey !== newTextureKey && newTextureKey) {
      this.updateTurretTexture(newTextureKey);
    }
  }

  private updateTurretTexture(textureKey: string): void {
    // Remove old turret sprite if exists
    if (this.turretSprite) {
      this.turretSprite.destroy();
      this.turretSprite = undefined;
    }

    // Remove old fallback graphics if exists
    if (this.turretGraphics) {
      this.turretGraphics.destroy();
      this.turretGraphics = undefined;
    }

    // Create new turret with new texture
    if (this.scene.textures.exists(textureKey)) {
      this.turretSprite = this.scene.add.sprite(0, 0, textureKey);
      this.turretSprite.setOrigin(0.5, 0.6);
      this.turretSprite.setScale(0.5);
      this.add(this.turretSprite);
    }
  }

  private getDefaultPathName(): string {
    switch (this.config.type) {
      case TowerType.CPU_MAIN:
        return 'CPU';
      case TowerType.LANGUAGE_SIDE:
        return 'C';
      case TowerType.NETWORK_SIDE:
        return 'Apache';
      default:
        return 'CPU';
    }
  }

  private createTurret(): void {
    const turretTexture = this.levelConfig?.textureKey ? `${this.levelConfig.textureKey}` : null;

    if (turretTexture && this.scene.textures.exists(turretTexture)) {
      this.turretSprite = this.scene.add.sprite(0, 0, turretTexture);
      this.turretSprite.setOrigin(0.5, 0.6);
      this.turretSprite.setScale(0.5); // Scale down the turret sprite
      this.add(this.turretSprite);
    } else {
      // Fallback to simple turret graphics - colored circle with barrel
      this.turretGraphics = this.scene.add.graphics();
      const color = this.getTurretColor();

      // Main turret body (circle)
      this.turretGraphics.fillStyle(color, 1);
      this.turretGraphics.fillCircle(0, 0, 18);

      // Turret barrel (pointing up by default)
      this.turretGraphics.fillStyle(0x3a362e, 1);
      this.turretGraphics.fillRect(-5, -28, 10, 24);

      // Inner highlight
      this.turretGraphics.fillStyle(Phaser.Display.Color.GetColor(
        ((color >> 16) & 0xff) + 40,
        ((color >> 8) & 0xff) + 40,
        (color & 0xff) + 40
      ), 0.6);
      this.turretGraphics.fillCircle(-4, -4, 6);

      this.add(this.turretGraphics);
    }
  }

  private createLevelIndicator(): void {
    this.levelIndicator = this.scene.add.text(0, 24, `Lv.${this.towerInstance.currentLevel}`, {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'monospace',
      stroke: '#000000',
      strokeThickness: 2,
    });
    this.levelIndicator.setOrigin(0.5, 0);
    this.add(this.levelIndicator);
  }

  private getTurretColor(): number {
    switch (this.config.type) {
      case TowerType.CPU_MAIN:
        return 0x5890e0;  // Blue for CPU
      case TowerType.LANGUAGE_SIDE:
        return 0x52c07a;  // Green for Language
      case TowerType.NETWORK_SIDE:
        return 0xe6b832;  // Yellow for Network
      default:
        return 0x6b6459;
    }
  }

  // Selection handling
  private onSelect(): void {
    this.isSelected = !this.isSelected;
    this.updateSelectionVisual();
    this.emit('select', { tower: this, isSelected: this.isSelected });
  }

  public clearSelection(): void {
    this.isSelected = false;
    this.updateSelectionVisual();
  }

  // Disable/enable interaction when upgrade panel is open
  setInteractionEnabled(enabled: boolean): void {
    this.setInteractive({ useHandCursor: enabled });
    this.setAlpha(enabled ? 1 : 0.7);
  }

  private updateSelectionVisual(): void {
    if (this.rangeIndicator) {
      this.rangeIndicator.destroy();
      this.rangeIndicator = undefined;
    }

    if (this.isSelected && this.levelConfig) {
      this.rangeIndicator = this.scene.add.graphics();
      this.rangeIndicator.lineStyle(2, 0xe6b832, 0.5);
      this.rangeIndicator.strokeCircle(0, 0, this.levelConfig.range);
      this.rangeIndicator.setDepth(-1);
      this.add(this.rangeIndicator);
    }
  }

  // Visual updates
  updateTurretRotation(targetX: number, targetY: number): void {
    if (this.turretSprite) {
      const angle = Phaser.Math.Angle.Between(this.x, this.y, targetX, targetY);
      this.turretSprite.setRotation(angle + Math.PI / 2);
    }
  }

  playAttackAnimation(): void {
    if (this.turretSprite) {
      this.scene.tweens.add({
        targets: this.turretSprite,
        scaleX: 1.2,
        scaleY: 1.2,
        duration: 50,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    }
  }

  // Upgrade visual feedback
  playUpgradeAnimation(): void {
    // Flash effect
    this.scene.tweens.add({
      targets: this,
      alpha: 0.5,
      duration: 100,
      yoyo: true,
      repeat: 1,
    });

    // Scale up
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 150,
      yoyo: true,
      ease: 'Back.easeOut',
    });

    // Update level indicator
    if (this.levelIndicator) {
      this.levelIndicator.setText(`Lv.${this.towerInstance.currentLevel}`);
    }
  }

  // Getters
  getId(): string {
    return this.config.id;
  }

  getType(): TowerType {
    return this.config.type;
  }

  getLevel(): number {
    return this.towerInstance.currentLevel;
  }

  getX(): number {
    return this.x;
  }

  getY(): number {
    return this.y;
  }

  getRange(): number {
    return this.levelConfig?.range || 150;
  }

  getDps(): number {
    return this.levelConfig?.dps || 10;
  }

  getUpgradeCost(): number {
    return this.levelConfig?.upgradeCost || 100;
  }

  // Level update (called after upgrade)
  updateLevel(level: number): void {
    this.towerInstance.currentLevel = level;
    this.updateLevelConfig();
    this.playUpgradeAnimation();
  }

  // Cleanup
  destroy(): void {
    if (this.rangeIndicator) {
      this.rangeIndicator.destroy();
    }
    super.destroy();
  }
}

// Factory function for creating towers
export function createTowerSprite(
  scene: Phaser.Scene,
  type: TowerType,
  x: number,
  y: number,
  id: string,
  pathName?: string
): TowerSprite {
  return new TowerSprite(scene, {
    id,
    type,
    x,
    y,
    pathName,
    scene,
  });
}
